import React from "react";

import { Grid } from "@mui/material";
import { Divider } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DifferentLocation, Location } from "../../../../../models/location";
import { CommercialInvoice } from "../../../../../util/enums";
import Input from "../../../../Form/Input";
import AddressInfoForm from "../../../../Generic/AddressComponent/AddressInfoForm";
import Skeleton from "../../../../Generic/Skeleton";
import useStyles from "../styles";
import { CommercialInvoiceFormProps } from "../types";

export const CommercialInvoiceAlternative = ({
  commercialInvoice,
  viewOnly,
  fieldHasError,
  getErrorByField,
  updateComercialInvoice,
  loadingBooking = true,
}: CommercialInvoiceFormProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Divider orientation={"horizontal"} flexItem className={classes.dividerHorizontal} />
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.BANK_NAME}
              name={CommercialInvoice.BANK_NAME}
              value={commercialInvoice?.bankName || ""}
              label={translate(CommercialInvoice.BANK_NAME)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.BANK_NAME, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.BANK_NAME)}
              errorMessage={getErrorByField(CommercialInvoice.BANK_NAME)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.BANK_NAME)}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Skeleton isLoading={loadingBooking}>
            <AddressInfoForm
              required
              dataCy={CommercialInvoice.BANK_ADDRESS}
              addressesArray={[(commercialInvoice?.bankAddress || []) as DifferentLocation]}
              view={viewOnly}
              showMapIcon={false}
              isExpandedAddress={true}
              condition=""
              update={(value) =>
                updateComercialInvoice(CommercialInvoice.BANK_ADDRESS, value[0] as Location)
              }
              hasToShowOnlyOne={true}
              title={translate(CommercialInvoice.BANK_ADDRESS)}
              hasError={fieldHasError(CommercialInvoice.BANK_ADDRESS)}
              errorMessage={getErrorByField(CommercialInvoice.BANK_ADDRESS)}
            />
          </Skeleton>
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.ACCOUNT_NUMBER}
              name={CommercialInvoice.ACCOUNT_NUMBER}
              value={commercialInvoice?.accountNumber || ""}
              label={translate(CommercialInvoice.ACCOUNT_NUMBER)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.ACCOUNT_NUMBER, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.ACCOUNT_NUMBER)}
              errorMessage={getErrorByField(CommercialInvoice.ACCOUNT_NUMBER)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.ACCOUNT_NUMBER)}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.SWIFT_CODE}
              name={CommercialInvoice.SWIFT_CODE}
              value={commercialInvoice?.swiftCode || ""}
              label={translate(CommercialInvoice.SWIFT_CODE)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.SWIFT_CODE, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.SWIFT_CODE)}
              errorMessage={getErrorByField(CommercialInvoice.SWIFT_CODE)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.SWIFT_CODE)}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.TERMS_OF_DELIVERY}
              name={CommercialInvoice.TERMS_OF_DELIVERY}
              value={commercialInvoice?.termsOfDelivery || ""}
              label={translate(CommercialInvoice.TERMS_OF_DELIVERY)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.TERMS_OF_DELIVERY, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.TERMS_OF_DELIVERY)}
              errorMessage={getErrorByField(CommercialInvoice.TERMS_OF_DELIVERY)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.TERMS_OF_DELIVERY)}
            />
          </Skeleton>
        </Grid>
      </Grid>
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.UD_NUMBER}
              name={CommercialInvoice.UD_NUMBER}
              value={commercialInvoice?.udNumber || ""}
              label={translate(CommercialInvoice.UD_NUMBER)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.UD_NUMBER, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.UD_NUMBER)}
              errorMessage={getErrorByField(CommercialInvoice.UD_NUMBER)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.UD_NUMBER)}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.ERC_NUMBER}
              name={CommercialInvoice.ERC_NUMBER}
              value={commercialInvoice?.ercNumber}
              label={translate(CommercialInvoice.ERC_NUMBER)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.ERC_NUMBER, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.ERC_NUMBER)}
              errorMessage={getErrorByField(CommercialInvoice.ERC_NUMBER)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.ERC_NUMBER)}
            />
          </Skeleton>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Skeleton isLoading={loadingBooking}>
            <Input
              required
              data-cy={CommercialInvoice.REX_NUMBER}
              name={CommercialInvoice.REX_NUMBER}
              value={commercialInvoice?.rexNumber || ""}
              label={translate(CommercialInvoice.REX_NUMBER)}
              onChange={(event) =>
                updateComercialInvoice(CommercialInvoice.REX_NUMBER, event.target.value)
              }
              error={fieldHasError(CommercialInvoice.REX_NUMBER)}
              errorMessage={getErrorByField(CommercialInvoice.REX_NUMBER)}
              disabled={viewOnly}
              placeholder={translate(CommercialInvoice.REX_NUMBER)}
            />
          </Skeleton>
        </Grid>
      </Grid>
    </Grid>
  );
};
